'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Metric {
  icon: string;
  value: string;
  label: string;
}

interface Certification {
  icon: string;
  title: string;
  description: string;
}

interface Disclaimer {
  icon: string;
  title: string;
  description: string;
}

interface TrustContent {
  sectionTitle: string;
  sectionDescription: string;
  metrics: Metric[];
  certifications: Certification[];
  disclaimer: Disclaimer;
}

interface TrustIndicatorsProps {
  className?: string;
}

const TrustIndicators = ({ className = '' }: TrustIndicatorsProps) => {
  const [content, setContent] = useState<TrustContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('content_pages')
        .select('content')
        .eq('slug', 'homepage_trust')
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;
      
      if (data?.content) {
        const parsedContent = typeof data.content === 'string' 
          ? JSON.parse(data.content) 
          : data.content;
        setContent(parsedContent);
      }
    } catch (error) {
      console.error('Error loading trust content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !content) {
    return (
      <section className={`bg-surface py-20 lg:py-28 ${className}`}>
        <div className="mx-auto px-6 lg:px-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-2/3 mx-auto"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`bg-surface py-20 lg:py-28 ${className}`}>
      <div className="mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-5xl text-primary mb-6">
            {content.sectionTitle}
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            {content.sectionDescription}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {content.metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-white border border-border rounded-sm p-6 text-center hover:shadow-institutional transition-shadow duration-smooth"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon
                  name={metric.icon as any}
                  size={24}
                  variant="solid"
                  className="text-accent"
                />
              </div>
              <div className="font-headline font-bold text-3xl text-primary mb-2">
                {metric.value}
              </div>
              <div className="font-body text-sm text-text-secondary">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          <h3 className="font-headline font-semibold text-2xl text-primary text-center mb-12">
            Regulatory Credibility
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white border border-border rounded-sm p-6 hover:shadow-institutional transition-shadow duration-smooth"
              >
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mb-4">
                  <Icon
                    name={cert.icon as any}
                    size={24}
                    variant="solid"
                    className="text-success"
                  />
                </div>
                <h4 className="font-headline font-semibold text-lg text-primary mb-3">
                  {cert.title}
                </h4>
                <p className="font-body text-sm text-text-secondary leading-relaxed">
                  {cert.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-white border-2 border-accent rounded-sm p-8 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                <Icon name={content.disclaimer.icon as any} size={32} variant="solid" className="text-accent" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h4 className="font-headline font-semibold text-xl text-primary mb-2">
                {content.disclaimer.title}
              </h4>
              <p className="font-body text-base text-text-secondary leading-relaxed">
                {content.disclaimer.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;